import { getFundingAccountTrackingFields } from 'components/Transfer/fundingAccountPolicy';
import { FE_SESSION_ID_KEY } from 'constants/session';
import { recordDepositMetric } from 'lib/metrics';
import type { CryptoProvider } from 'models/crypto';
import type { DepositStep, DepositStepName } from 'models/metric';
import { FundMethods } from 'stores/zustand/registrationStore';
import { useTransferDialogStore } from 'stores/zustand/transferDialogStore';
import {
    getLocalStorageItem,
    getSessionStorageItem,
    removeSessionStorageItem,
    setSessionStorageItem,
} from 'utils/storage';
import { uuidv4 } from 'utils/uuid';

const DEPOSIT_FLOW_ID_KEY = 'kalshi-deposit-flow-id';
const DEPOSIT_FUNDING_CONTEXT_KEY = 'kalshi-deposit-funding-context';

export type DepositPaymentMethod = NonNullable<DepositStep['payment_method']>;
export type DepositRail = NonNullable<DepositStep['rail']>;
export type DepositProvider = NonNullable<DepositStep['provider']>;
export type DepositAmountSource = NonNullable<DepositStep['amount_source']>;
export type DepositTrackingContext = Omit<DepositStep, 'step' | 'funnel_name' | 'funnel_version'>;

export interface DepositWalletFlags {
    isApplePayOnly?: boolean;
    isGooglePayOnly?: boolean;
    isCashAppOnly?: boolean;
}

type DepositFundingTrackingContext = Required<Pick<DepositTrackingContext, 'account_mode' | 'resolved_destination'>>;

function isDepositFundingTrackingContext(value: unknown): value is DepositFundingTrackingContext {
    if (!value || typeof value !== 'object') return false;
    const context = value as Partial<DepositFundingTrackingContext>;
    return (
        (context.account_mode === 'standard' ||
            context.account_mode === 'perps_only' ||
            context.account_mode === 'unknown') &&
        (context.resolved_destination === 'event_contract' ||
            context.resolved_destination === 'margin' ||
            context.resolved_destination === 'unknown')
    );
}

function readDepositFundingTrackingContext(): DepositFundingTrackingContext | undefined {
    const stored = getSessionStorageItem(DEPOSIT_FUNDING_CONTEXT_KEY);
    if (!stored) return undefined;

    try {
        const parsed: unknown = JSON.parse(stored);
        return isDepositFundingTrackingContext(parsed) ? parsed : undefined;
    } catch {
        return undefined;
    }
}

function writeDepositFundingTrackingContext(context: DepositFundingTrackingContext): void {
    setSessionStorageItem(DEPOSIT_FUNDING_CONTEXT_KEY, JSON.stringify(context));
}

interface BuildDepositTrackingContextOptions {
    paymentMethod?: DepositPaymentMethod;
    fundMethod?: FundMethods;
    rail?: DepositRail;
    provider?: DepositProvider;
    walletFlags?: DepositWalletFlags;
    pathname?: string | null;
    pageLoadId?: string | null;
    amountInDollars?: number;
    amountSource?: DepositAmountSource;
    savedPaymentMethod?: boolean;
    hasDepositedBefore?: boolean;
    isSignup?: boolean;
}

export function startDepositFlow(): string {
    const depositFlowId = uuidv4();
    removeSessionStorageItem(DEPOSIT_FUNDING_CONTEXT_KEY);
    setSessionStorageItem(DEPOSIT_FLOW_ID_KEY, depositFlowId);
    return depositFlowId;
}

export function ensureDepositFlowId(): string {
    const existing = getSessionStorageItem(DEPOSIT_FLOW_ID_KEY);
    if (existing) {
        return existing;
    }

    const depositFlowId = uuidv4();
    setSessionStorageItem(DEPOSIT_FLOW_ID_KEY, depositFlowId);
    return depositFlowId;
}

export function getExistingDepositFlowId(): string | undefined {
    return getSessionStorageItem(DEPOSIT_FLOW_ID_KEY) || undefined;
}

export function clearDepositFlowId(): void {
    removeSessionStorageItem(DEPOSIT_FLOW_ID_KEY);
    removeSessionStorageItem(DEPOSIT_FUNDING_CONTEXT_KEY);
}

export function getFrontendSessionId(): string | undefined {
    return getLocalStorageItem(FE_SESSION_ID_KEY) || undefined;
}

export function getDepositRequestTrackingProps(): Pick<DepositStep, 'deposit_flow_id' | 'frontend_session_id'> {
    return {
        deposit_flow_id: ensureDepositFlowId(),
        frontend_session_id: getFrontendSessionId(),
    };
}

// Maps the FE-facing crypto vendor (which may include 'stripe' from the
// rail-resolution helper) to the analytics-schema provider value. Mesh and
// Coinbase share the 'mesh' provider in tracking; Zerohash and Stripe each
// have their own. Returns 'unknown' for null/unrecognized so the caller
// never silently mis-attributes (used to fall through to 'zerohash').
type CryptoTrackingProvider = Extract<DepositProvider, 'mesh' | 'zerohash' | 'stripe' | 'unknown'>;

export function getCryptoDepositTrackingProvider(
    provider: CryptoProvider | 'stripe' | null | undefined,
): CryptoTrackingProvider {
    switch (provider) {
        case 'stripe':
            return 'stripe';
        case 'zerohash':
            return 'zerohash';
        case 'coinbase':
        case 'mesh':
            return 'mesh';
        default:
            return 'unknown';
    }
}

export function amountToCents(amountInDollars: number): number {
    return Math.round(amountInDollars * 100);
}

export function getDepositLimitViewedDedupeKey(context: DepositTrackingContext): string {
    return [
        context.payment_method,
        context.rail,
        context.provider,
        context.limit_type,
        context.limit_scope,
        context.deposit_limit_cents,
        context.deposit_limit_remaining_cents,
        context.deposit_limit_reason,
        context.limit_exceeded,
        context.error_source ?? 'validation',
    ]
        .map((value) => String(value ?? ''))
        .join('|');
}

export function getDepositAmountEnteredDedupeKey(context: DepositTrackingContext): string {
    return [context.payment_method, context.rail, context.provider, context.amount_cents, context.amount_source]
        .map((value) => String(value ?? ''))
        .join('|');
}

export function trackDepositAmountEntered(
    context: DepositTrackingContext,
    amountSource: DepositAmountSource = 'typed',
): void {
    trackDepositStep('amountEntered', {
        ...context,
        amount_source: amountSource,
    });
}

export function trackDepositLimitViewed(context: DepositTrackingContext): void {
    trackDepositStep('depositLimitViewed', {
        ...context,
        error_source: context.error_source ?? 'validation',
    });
}

export function getDepositSurface(pathname?: string | null): string {
    if (pathname?.includes('/sign-up')) {
        return 'signup';
    }
    return 'deposit_modal';
}

export function trackDepositStep(step: DepositStepName, context: DepositTrackingContext = {}): void {
    const { deposit_flow_id, frontend_session_id, ...eventContext } = context;

    recordDepositMetric({
        step,
        deposit_flow_id: deposit_flow_id ?? ensureDepositFlowId(),
        frontend_session_id: frontend_session_id ?? getFrontendSessionId(),
        funnel_name: context.is_first_deposit_context ? 'first_deposit' : 'deposit',
        funnel_version: 'deposit_funnel_v1',
        ...eventContext,
    });
}

export function getDepositMethodContext(
    method: DepositPaymentMethod,
): Required<Pick<DepositTrackingContext, 'payment_method' | 'rail' | 'provider'>> {
    switch (method) {
        case 'bank':
            return { payment_method: method, rail: 'ach', provider: 'plaid' };
        case 'card':
        case 'apple_pay':
        case 'google_pay':
            return { payment_method: method, rail: 'debit', provider: 'stripe' };
        case 'paypal':
        case 'venmo':
            return { payment_method: method, rail: 'apm', provider: 'braintree' };
        case 'cashapp':
            return { payment_method: method, rail: 'apm', provider: 'stripe' };
        case 'crypto':
            return { payment_method: method, rail: 'crypto', provider: 'zerohash' };
        case 'wire':
            return { payment_method: method, rail: 'wire', provider: 'manual_wire' };
        default:
            return { payment_method: 'unknown', rail: 'unknown', provider: 'unknown' };
    }
}

export function getDepositMethodContextForFundMethod(
    fundMethod: FundMethods | undefined,
    walletFlags: DepositWalletFlags = {},
): Required<Pick<DepositTrackingContext, 'payment_method' | 'rail' | 'provider'>> {
    switch (fundMethod) {
        case FundMethods.BANK:
            return getDepositMethodContext('bank');
        case FundMethods.WIRE:
            return getDepositMethodContext('wire');
        case FundMethods.CRYPTO:
            return getDepositMethodContext('crypto');
        case FundMethods.CASHAPP:
            return getDepositMethodContext('cashapp');
        case FundMethods.BRAINTREE_PAYPAL:
            return getDepositMethodContext('paypal');
        case FundMethods.BRAINTREE_VENMO:
            return getDepositMethodContext('venmo');
        case FundMethods.DEBIT_CARD:
            if (walletFlags.isApplePayOnly) {
                return getDepositMethodContext('apple_pay');
            }
            if (walletFlags.isGooglePayOnly) {
                return getDepositMethodContext('google_pay');
            }
            return getDepositMethodContext('card');
        default:
            return getDepositMethodContext('unknown');
    }
}

export function buildDepositTrackingContext({
    paymentMethod,
    fundMethod,
    rail,
    provider,
    walletFlags,
    pathname,
    pageLoadId,
    amountInDollars,
    amountSource,
    savedPaymentMethod,
    hasDepositedBefore,
    isSignup,
}: BuildDepositTrackingContextOptions): DepositTrackingContext {
    const methodContext = paymentMethod
        ? getDepositMethodContext(paymentMethod)
        : getDepositMethodContextForFundMethod(fundMethod, walletFlags);
    const transferDialog = useTransferDialogStore.getState();
    const liveAccountContext = getFundingAccountTrackingFields(transferDialog.depositDestination);
    const shouldRefreshFundingSnapshot =
        transferDialog.isOpen || isSignup === true || pathname?.includes('/sign-up') === true;
    const accountContext = shouldRefreshFundingSnapshot
        ? liveAccountContext
        : (readDepositFundingTrackingContext() ?? liveAccountContext);

    if (shouldRefreshFundingSnapshot) {
        writeDepositFundingTrackingContext(liveAccountContext);
    }

    return {
        ...methodContext,
        ...accountContext,
        rail: rail ?? methodContext.rail,
        provider: provider ?? methodContext.provider,
        surface: getDepositSurface(pathname),
        page_load_id: pageLoadId || undefined,
        amount_cents: amountInDollars !== undefined && amountInDollars > 0 ? amountToCents(amountInDollars) : undefined,
        amount_source: amountSource,
        saved_payment_method: savedPaymentMethod,
        is_signup: isSignup ?? pathname?.includes('/sign-up') ?? false,
        is_first_deposit_context: hasDepositedBefore === undefined ? undefined : !hasDepositedBefore,
    };
}
